import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';

import { DisposalCategoryPricingDocument } from '@graphql/platform';

import { formatCurrency } from '@utils/formatters';

import closeIcon from '@images/checkout/challenger/close.svg';

import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

/**
 * Figma overlay — stacks above mobile bottom drawer (annotation on 707:17936).
 * Portaled to document.body so parent overflow cannot clip the header.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: safe center;
  justify-content: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

/**
 * Desktop 707:12082 — 560×685, pad 24.
 * Mobile 707:17936 — 370×658.5, pad 16 (full width minus overlay gutters).
 */
const Dialog = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
  overflow: hidden;

  ${MOBILE} {
    max-width: 370px;
    height: 658.5px;
    max-height: min(658.5px, calc(100vh - 32px));
    max-height: min(658.5px, calc(100dvh - 32px));
    padding: ${T.space[16]};
  }

  ${DESKTOP} {
    width: 560px;
    max-width: calc(100vw - 32px);
    height: 685px;
    max-height: min(685px, calc(100vh - 32px));
    max-height: min(685px, calc(100dvh - 32px));
    padding: ${T.space[24]};
  }
`;

/** Container — gap 16. */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  height: 100%;
  min-height: 0;
`;

/** Sticky header above scroll. */
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${T.space[8]};
  width: 100%;
  flex-shrink: 0;
`;

/**
 * Desktop 707:12085 — Medium 20 / 23.
 * Mobile 707:17939 — Medium 16 / 22.
 */
const Title = styled.h2`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }

  ${DESKTOP} {
    max-width: 336px;
    flex: 0 1 auto;
    font-size: 20px;
    line-height: 23px;
  }
`;

/** Close Icon 24×24; vector inset 20.83% → 14×14 leaf. */
const CloseButton = styled.button`
  appearance: none;
  position: relative;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;

  img {
    position: absolute;
    inset: 20.83%;
    display: block;
    width: 14px;
    height: 14px;
  }
`;

/** Scroll viewport — gap 16, pb 24; 6px Figma scrollbar. */
const ScrollBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
  min-height: 0;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: ${T.space[24]};
  box-sizing: border-box;

  scrollbar-width: thin;
  scrollbar-color: ${T.color.textSubtle} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${T.color.textSubtle};
    border-radius: ${T.radius.xs};
  }
`;

/** Intro + table, gap 16, pb 8. */
const PricingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  padding-bottom: ${T.space[8]};
  box-sizing: border-box;
`;

/**
 * Funnel-Feedback 24:988 — Regular 14 / 20.
 * Mobile 707:17942 — Regular 13 / 18.
 */
const Intro = styled.p`
  margin: 0;
  width: 100%;
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    font-weight: ${FontWeight.Regular};
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    font-weight: ${FontWeight.Regular};
    line-height: 20px;
  }
`;

const RateTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: ${T.radius.md};
  overflow: hidden;
`;

/** Figma 29:3077 — Item max 164px, Price flex-fills and centers (not flush right). */
const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[24]};
  width: 100%;
  padding: ${T.space[16]} ${T.space[16]} ${T.space[12]};
  box-sizing: border-box;
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.sm} ${T.radius.sm} 0 0;
`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 ${T.space[8]};
  box-sizing: border-box;
  background: ${T.color.summarySurface};

  &:last-of-type {
    border-radius: 0 0 ${T.radius.sm} ${T.radius.sm};
  }
`;

const TableRowInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[24]};
  width: 100%;
  padding: ${T.space[16]} ${T.space[8]};
  box-sizing: border-box;
  border-top: 1px solid ${T.color.borderDefault};
`;

/** Table header — Figma 29:3077 Body/Large 600 (16/24 desktop, 14/20 mobile). */
const HeaderItem = styled.div`
  flex: 1 0 0;
  max-width: 164px;
  min-width: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const HeaderPrice = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  text-align: center;

  ${MOBILE} {
    font-size: 14px;
    line-height: 20px;
  }

  ${DESKTOP} {
    font-size: 16px;
    line-height: 24px;
  }
`;

/** Row cells — Figma 29:3078 Body/Medium 600 (14/21 desktop, 13/18 mobile). */
const ItemCell = styled.div`
  flex: 1 0 0;
  max-width: 164px;
  min-width: 0;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const PriceCell = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};
  text-align: center;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 21px;
  }
`;

const ProhibitedBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
`;

/**
 * Desktop — Medium 20 / 24.
 * Mobile 707:17950 — Medium 16 / 22.
 */
const SectionTitle = styled.h3`
  margin: 0;
  width: 100%;
  font-weight: ${FontWeight.Medium};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 16px;
    line-height: 22px;
  }

  ${DESKTOP} {
    font-size: 20px;
    line-height: 24px;
  }
`;

/**
 * Desktop — Regular 14 / 20.
 * Mobile 707:17951 — Regular 13 / 18.
 */
const ProhibitedIntro = styled.p`
  margin: 0;
  width: 100%;
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

/**
 * Desktop — Regular 14 / 20, indent 21.
 * Mobile 707:17952 — Regular 13 / 18, indent 19.5.
 */
const ProhibitedList = styled.ul`
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  list-style: disc;
  font-weight: ${FontWeight.Regular};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    padding: 0 0 0 19.5px;
    font-size: 13px;
    line-height: 18px;
  }

  ${DESKTOP} {
    padding: 0 0 0 21px;
    font-size: 14px;
    line-height: 20px;
  }

  li + li {
    margin-top: 0;
  }
`;

/** Funnel-Feedback 24:989 — API category strings mapped to modal chart labels. */
const DISPOSAL_CATEGORY_CHART_LABELS: Record<string, string> = {
  'Couch/Loveseat': 'Couch or Loveseat',
  'Television - Flat Screen, < 42”': '<42" Flat Screen TV',
  'Television - Flat Screen, < 42"': '<42" Flat Screen TV',
  'Television - Flat Screen, >= 42”': '42"+ Flat Screen TV',
  'Television - Flat Screen, >= 42"': '42"+ Flat Screen TV',
};

const disposalCategoryChartLabel = (category: string): string =>
  DISPOSAL_CATEGORY_CHART_LABELS[category] ?? category;

/** Funnel-Feedback 24:998 — disposal restrictions list. */
const PROHIBITED_ITEMS = [
  'Liquids of any kind including alcohol, cleaning products, cosmetics, toiletries, etc',
  'Flammable or combustible materials, including gasses, explosives and asbestos',
  'Hazardous materials including, radioactive waste, chemical waste or biological waste',
  'Illegal or controlled substances',
  'Items that show signs of infestation or are at a location with signs of infestation including insects, rodents, mold, etc.',
  'Items that are unsafe to handle and transport, including unusually heavy or bulky items',
] as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const DisposalPricingModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useModalFocus(isOpen, dialogRef, closeButtonRef);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data, loading, error } = useQuery(DisposalCategoryPricingDocument, {
    skip: !isOpen,
  });
  const rows = data?.disposalCategoryPricing ?? [];

  useEffect(() => {
    if (!isOpen) return;

    scrollRef.current?.scrollTo(0, 0);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  return createPortal(
    <Overlay onClick={onOverlayClick} role="presentation">
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-disposal-pricing-title"
        onClick={(event) => event.stopPropagation()}
      >
        <Container>
          <Header>
            <Title id="challenger-disposal-pricing-title">
              Disposal pricing
            </Title>
            <CloseButton
              ref={closeButtonRef}
              type="button"
              aria-label="Close"
              onClick={onClose}
            >
              <img src={closeIcon} alt="" width={14} height={14} />
            </CloseButton>
          </Header>

          <ScrollBody ref={scrollRef}>
            <PricingBlock>
              <Intro>
                Disposal is $50 plus $3.50 per cuft. The following items are a
                flat price instead of $3.50 per cuft.
              </Intro>

              {loading && <Intro>Loading disposal pricing…</Intro>}
              {!loading && error && (
                <Intro>
                  Unable to load disposal pricing. Please try again.
                </Intro>
              )}
              {!loading && !error && rows.length > 0 && (
                <RateTable>
                  <TableHeader>
                    <HeaderItem>Item</HeaderItem>
                    <HeaderPrice>Price</HeaderPrice>
                  </TableHeader>
                  {rows.map((row) => (
                    <TableRow key={row.category}>
                      <TableRowInner>
                        <ItemCell>
                          {disposalCategoryChartLabel(row.category)}
                        </ItemCell>
                        <PriceCell>
                          {formatCurrency(row.minimum, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </PriceCell>
                      </TableRowInner>
                    </TableRow>
                  ))}
                </RateTable>
              )}
            </PricingBlock>

            <ProhibitedBlock>
              <SectionTitle>Disposal restrictions</SectionTitle>
              <ProhibitedIntro>
                We cannot dispose of the following items:
              </ProhibitedIntro>
              <ProhibitedList>
                {PROHIBITED_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ProhibitedList>
            </ProhibitedBlock>
          </ScrollBody>
        </Container>
      </Dialog>
    </Overlay>,
    document.body,
  );
};
